import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { clamp } from 'utils/math';
import { findOptionTags, hasInVNode, replaceOptionTags, replaceWithVNode } from 'utils/vnode';
import { escapeSpecialChars } from 'utils/toRegExp';

import Button from 'components/button/button';

import s from 'components/dropdown/dropdown.sss';

// NOTE: should we provide a way to clear the selection if props.allowEmpty is true ? Native select elements do not (they can start empty but once a value is selected it cannot be cleared)
//   or should we get rid of the allowEmpty option and make it always behave that way (empty initially, unless a value is set)
export default class Dropdown extends Component {
	s = s;

	constructor(props) {
		super(props);

		let value = this.sanitizeValue(props.value ?? props.initialValue);
		this.state = {
			value,
			search: '',
		};

		this.onBlur = this.onBlur.bind(this);
		this.onClear = this.onClear.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onToggle = this.onToggle.bind(this);
		this.close = this.close.bind(this);
		this.onSelectionChanged = this.onSelectionChanged.bind(this);
		this.onSearchInput = this.onSearchInput.bind(this);
		this.renderOption = this.renderOption.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		const options = this.getOptions(nextProps);
		let nextValue = this.sanitizeValue(nextProps.value !== this.props.value ? nextProps.value : this.state.value, options);
		if (nextValue !== this.state.value) {
			this.setInternalValue(nextValue, nextProps);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.value !== this.state.value) {
			this.scrollToSelectedItem();
		}
	}

	get value() {
		return this.state.value;
	}
	set value(v) {
		let nextValue = this.sanitizeValue(v);
		if (nextValue !== this.state.value) {
			this.setInternalValue(nextValue);
		}
	}

	get name() {
		return this.props.name;
	}

	setInternalValue(value) {
		this.setState({ value });
	}

	get details() {
		return this.props.details;
	}

	sanitizeValue(value, options = this.getOptions()) {
		if (options.includes(value)) return value;
		if (this.props.allowEmpty) return null;
		return options[0] || value; // If no options, keep the value as is incase options are loaded later
	}

	getOptions(props = this.props) {
		return this.findOptions(props).map(option => option.attributes.value);
	}

	getSelectedItem() {
		return this.findOptions().find(option => option.attributes.value === this.state.value);
	}

	getLabelForValue(value, props = this.props) {
		const option = this.findOptions(props).find(opt => opt.attributes.value === value);
		return option?.children || '';
	}

	findOptions(props = this.props) {
		return findOptionTags(props.children);
	}

	renderOptions(props = this.props) {
		return replaceOptionTags(props.children, this.renderOption);
	}

	scrollToSelectedItem() {
		try {
			this.base.querySelector(`[popover] input:checked`)?.scrollIntoView({ block: 'nearest' });
		} catch {}
	}

	// Clicking out automatically closes the popover, but tabbing away doesn't
	// So handle it manually
	onBlur(e) {
		if (this.base.contains(e.relatedTarget)) return;

		// Still have to check for the actual focus to change, sometimes relatedTarget is null
		setTimeout(() => {
			if (this.base && !this.base.contains(document.activeElement)) {
				this.close();
			}
		}, 0);
	}

	onClear(e) {
		e.stopPropagation();
		e.preventDefault();
		this.setValue(null);
	}

	onKeyDown(e) {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			e.stopPropagation();
			const fromSearch = e.currentTarget?.matches(`.${this.s.search}`);

			const state = this.state;
			let options = this.findOptions();
			if (state.search) {
				options = options.filter(opt => hasInVNode(opt, state.search, { ignoreCase: true }));
			}
			const optionValues = options.map(option => option.attributes.value);
			const currentIndex = optionValues.indexOf(state.value);
			const newIndex = clamp(currentIndex + (e.key === 'ArrowDown' ? 1 : -1), 0, optionValues.length - 1);

			this.setValue(optionValues[newIndex], fromSearch);
		} else if (e.key === 'Enter' && e.currentTarget?.matches(`.${this.s.search}`)) {
			e.preventDefault();
			e.stopPropagation();
			this.close();
			this.setState({ search: '' });
		}
	}

	onToggle(e) {
		if (e.newState === 'open') {
			this.oldValueOnOpen = this.value;
			this.scrollToSelectedItem();
		}
		if (e.newState === 'closed' && this.value !== this.oldValueOnOpen) {
			this.oldValueOnOpen = undefined;
			this.triggerConfirmedChange();
		}
	}

	triggerChange() {
		this.props.onChange?.({ target: this });
	}
	triggerConfirmedChange() {
		this.props.onConfirmedChange?.({ target: this });
	}

	setValue(value, keepSearch) {
		if (value === this.state.value) {
			return;
		}
		this.setInternalValue(value);
		if (!keepSearch) {
			this.setState({ search: '' });
		}
		this.triggerChange();
		const popover = this.base.querySelector('[popover]');
		if (popover && !popover.matches(':popover-open')) {
			this.triggerConfirmedChange();
		}
	}

	close() {
		const popover = this.base.querySelector('[popover]');
		if (popover?.matches(':popover-open')) {
			popover.hidePopover();
		}
	}

	onSelectionChanged(e) {
		this.setValue(e.target.value);
	}

	onSearchInput(e) {
		this.setState({ search: e.target.value });
	}

	renderOption(element) {
		let content = element.children;
		const search = this.state.search;
		if (search) {
			let found = false;
			// Do not use the default string search behaviour as it adds the g flag but we want to stop at the first match
			const regex = new RegExp(escapeSpecialChars(search), 'i');
			// Messy, but we try to allow any HTML, so that makes it a bit tricky
			content = replaceWithVNode(content, regex, ([val]) => (found = true) && <em class={this.s.highlight}>{val}</em>);
			if (!found) {
				// Should we also search in the item's value ?
				// if (!regex.test(element.attributes.value)) return null;
				return null;
			}
		}
		return (
			<Button
				tag="label"
				icon={element.attributes.icon}
				iconAlt="check"
				iconSize="small"
				class={joinClasses(this.s.option, element.attributes.class)}
				onClick={this.close}
			>
				<input
					type="radio"
					name={this.props.name}
					value={element.attributes.value}
					checked={element.attributes.value === this.state.value}
					disabled={element.attributes.disabled}
					onChange={this.onSelectionChanged}
					tabIndex="-1"
				/>
				<div class={this.s.content}>
					{content}
				</div>
			</Button>
		);
	}

	renderLabel() {
		const props = this.props;
		let label = props.label;
		let labelClass;
		if (!label) {
			const selectedItem = this.getSelectedItem();
			if (selectedItem) {
				label = selectedItem.children;
				labelClass = selectedItem.attributes.class;
			} else {
				label = props.placeholder;
				labelClass = this.s.placeholder;
			}
		}
		return <div class={joinClasses(this.s.active, labelClass)}>{label}</div>;
	}

	renderPopoverContent() {
		const { state, props } = this;
		return [
			<input type="text" class={this.s.search} value={state.search} placeholder={props.searchPlaceholder} onInput={this.onSearchInput} onKeyDown={this.onKeyDown} autofocus />,
			<hr />,
			<div class={this.s.list} data-search={state.search} tabIndex="-1">
				{this.renderOptions()}
			</div>,
		];
	}

	render(props, state) {
		const popoverId = `${props.id || props.name}-dropdown-items`;
		const showClear = props.allowClear && !!state.value;
		return (
			<Button
				class={joinClasses(this.s.dropdown, this.s[`align-${props.align}`], props.searchable && this.s.searchable, props.hideSearchDivider && this.s.hideSearchDivider, props.class)}
				type="button"
				popovertarget={popoverId}
				disabled={props.disabled}
				icon={props.icon}
				iconAlt={props.noChevron ? 'none' : 'chevron-down'}
				iconSize="small"
				counter={props.counter}
				onFocusOut={this.onBlur}
				onKeyDown={this.onKeyDown}
				dark={props.dark}
				outline={props.outline}
				primary={props.primary}
				wrapper-data-value={state.value}
			>
				{this.renderLabel()}
				{showClear && <Button class={this.s.clear} icon="cancel" onClick={this.onClear} />}
				<div popover="auto" id={popoverId} onToggle={this.onToggle} onFocusOut={this.onBlur}>
					{this.renderPopoverContent()}
				</div>
			</Button>
		);
	}

}